import { z } from 'zod';

import { zFirestampNow, zFirestampOptional } from '@elara/firebolt/zod-types';

import { CoordinatesSchema } from './shared-schemas.js';

/**
 * State/Province Code Schema
 * Accepts both string and number types for flexibility.
 * When used in destination data, numbers are transformed to strings during parsing.
 */
export const StateCodeSchema = z.union([z.string(), z.number()]);

/**
 * Flight Info Schema
 */
export const FlightInfoSchema = z.looseObject({
  origin: z.string(),
  destination: z.string(),
  duration: z.object({ hours: z.number(), minutes: z.number() }),
  nonstop: z.boolean(),
  airlines: z.array(z.string()),
  averagePrice: z.object({
    economy: z.number(),
    business: z.number().optional(),
    first: z.number().optional(),
    currency: z.string().default('USD'),
  }),
});

export interface FlightInfo extends z.infer<typeof FlightInfoSchema> {}

/**
 * Emergency Numbers Schema
 */
export const EmergencyNumbersSchema = z.looseObject({
  fire: z.union([z.number(), z.string()]),
  medical: z.union([z.number(), z.string()]),
  police: z.union([z.number(), z.string()]),
});
export interface EmergencyNumbers extends z.infer<typeof EmergencyNumbersSchema> {}

/**
 * Power Outlet Info Schema
 */
export const PowerOutletInfoSchema = z.looseObject({
  frequency: z.string(),
  type: z.array(z.string()),
  voltage: z.union([z.string(), z.number()]).transform(String),
});
export interface PowerOutletInfo extends z.infer<typeof PowerOutletInfoSchema> {}

/**
 * Transportation Schema
 */
export const TransportationSchema = z.looseObject({
  primaryAirportCode: z.string().optional(),
  secondaryAirportCodes: z.array(z.string()).optional(),
});
export interface Transportation extends z.infer<typeof TransportationSchema> {}

/**
 * Logistics Schema
 */
export const LogisticsSchema = z.looseObject({
  accessibility: z.string().optional(),
  currency: z.string().optional(),
  emergencyNumbers: EmergencyNumbersSchema.optional(),
  powerOutletInfo: PowerOutletInfoSchema.optional(),
  primaryLanguages: z.array(z.string()),
  timeZone: z.string(),
  tippingCulture: z.string().optional(),
  transportation: TransportationSchema.optional(),
  travel_advisories: z.string().optional(),
});
export interface Logistics extends z.infer<typeof LogisticsSchema> {}

/**
 * Location Details Schema (different from coordinates)
 */
export const LocationDetailsSchema = z.looseObject({
  continent: z.string(),
  countryCode: z.string(),
  countryName: z.string(),
  stateProvince: z.string().nullable().optional(),
  stateProvinceCode: StateCodeSchema.transform((val) => (typeof val === 'number' ? String(val) : val))
    .nullable()
    .optional(),
  subregion: z.string().optional(),
  travelRegion: z.string(),
});
export interface LocationDetails extends z.infer<typeof LocationDetailsSchema> {}

/**
 * Key Event Schema
 * Note: dates can be either a string (e.g., "October 2025") or a Firestore Timestamp
 */
export const KeyEventSchema = z.looseObject({
  dates: z.union([z.string(), zFirestampOptional()]).transform((val) => {
    // If it's already a string, return as-is
    if (typeof val === 'string') return val;
    // If it's a Date (from zFirestampOptional), convert to ISO string
    if (val instanceof Date) return val.toISOString();
    // Otherwise undefined
    return undefined;
  }),
  eventName: z.string(),
});
export interface KeyEvent extends z.infer<typeof KeyEventSchema> {}

/**
 * Seasons Schema
 */
export const SeasonsSchema = z.looseObject({
  off_peak: z.string().optional(),
  peak: z.string().optional(),
  shoulder: z.string().optional(),
  super_peak: z.string().optional(),
});
export interface Seasons extends z.infer<typeof SeasonsSchema> {}

/**
 * Image schema
 */
export const DestinationImageSchema = z.looseObject({
  url: z.string().url(),
  caption: z.string().optional(),
  attribution: z
    .object({
      source: z.string().nullable().optional(),
      // Accept numbers and coerce to strings (data quality issue in some documents)
      credit: z
        .union([z.string(), z.number()])
        .transform((val) => (typeof val === 'number' ? String(val) : val))
        .nullable()
        .optional(),
      url: z.string().url().nullable().optional(),
    })
    .nullable()
    .optional(),
  created: zFirestampNow().optional(),
});
export interface DestinationImage extends z.infer<typeof DestinationImageSchema> {}

/**
 * Destination Schema - Complete with all fields from Firestore
 */
export const DestinationSchema = z.looseObject({
  // Core identification
  id: z.string().optional(),
  name: z.string(),

  // Alternative names and categorization. `z.coerce.string()` keeps one stray non-string entry (some
  // destination docs have a number in here) from failing the whole document parse.
  alternateNames: z.array(z.coerce.string()).nullable().optional(),
  collection: z.array(z.string()).optional(),

  // Experience and activities
  experience: z.array(z.string()).optional(),
  experience_detail: z.array(z.string()).optional(),

  // Location information
  location: LocationDetailsSchema.optional(),
  coordinates: CoordinatesSchema.optional(), // Keep for backward compatibility

  // Travel logistics
  logistics: LogisticsSchema.optional(),

  // Descriptions
  shortDescription: z.string().optional(),
  longDescription: z.string().optional(),
  description: z.string().optional(), // Keep for backward compatibility
  family_detail: z.string().optional(),
  considerations_summary: z.string().optional(),
  ideal_traveler_profile: z.string().optional(),

  // Atmosphere and setting
  mood_vibe_detail: z.array(z.string()).optional(),
  setting: z.array(z.string()).optional(),
  setting_detail: z.array(z.string()).optional(),

  // Events and seasons
  key_events: z
    .union([z.array(KeyEventSchema), z.array(z.string())])
    .transform((val) => {
      // If it's an array of strings, convert to KeyEvent objects
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
        return (val as string[]).map((eventName) => ({ eventName, dates: undefined }));
      }
      // Otherwise return as-is (array of KeyEvent objects)
      return val;
    })
    .nullable()
    .optional(),
  seasons: SeasonsSchema.optional(),
  weather_seasons: z.string().optional(),

  // Legacy fields for backward compatibility
  country: z.string().optional(),
  region: z.string().optional(),
  climate: z
    .object({
      type: z.enum(['tropical', 'temperate', 'arid', 'continental', 'polar']),
      bestMonths: z.array(z.number().min(1).max(12)),
      averageTemp: z.object({
        summer: z.object({ min: z.number(), max: z.number() }),
        winter: z.object({ min: z.number(), max: z.number() }),
      }),
    })
    .optional(),
  themes: z
    .array(
      z.enum(['beach', 'adventure', 'family', 'ski', 'culture', 'luxury', 'wellness', 'culinary', 'safari', 'island']),
    )
    .optional(),
  highlights: z.array(z.string()).optional(),
  images: z.array(DestinationImageSchema).optional(),
  flightInfo: z.array(FlightInfoSchema).optional(),
  hotels: z.array(z.string()).optional(), // Hotel IDs
  suggestedDuration: z.object({ min: z.number(), max: z.number(), unit: z.enum(['days', 'nights']) }).optional(),
  // Elara editorial notes (admin-only, not shown to users or agents)
  internalNotes: z.string().nullish().describe('Private admin notes not visible to users or agents'),
  elaraNotes: z.string().nullish().describe('Elara editorial notes for content curation'),

  /** Whether this destination is active and visible in search results. Treated as true when absent. */
  isActive: z.boolean().default(true),
  verified: z.boolean().default(false).optional(),
  lastUpdated: zFirestampNow().optional(),
});

export type DestinationSchema = typeof DestinationSchema;
export interface Destination extends z.infer<DestinationSchema> {}

/**
 * Content Source Schema (for subcollection)
 * URLs are Base64-encoded in the actual data
 */
export const ContentSourceSchema = z.looseObject({
  url: z.string(), // Base64-encoded URL
  title: z.string().optional(),
  description: z.string().optional(),
});
export interface ContentSource extends z.infer<typeof ContentSourceSchema> {}
